import {
  NoInnerEventsComponent
} from "./chunk.54YXW2TU.js";
import {
  emit,
  waitForEvent
} from "./chunk.HM4ZRC4T.js";
import {
  watch
} from "./chunk.HBM5K2JV.js";
import {
  __decorateClass,
  __spreadProps,
  __spreadValues,
  component_styles_default,
  theme_styles_default
} from "./chunk.U4VUMZVF.js";

// src/utilities/animate.ts
function animateTo(el, keyframes, options) {
  return new Promise(async (resolve) => {
    if ((options == null ? void 0 : options.duration) === Infinity) {
      throw new Error("Promise-based animations must be finite.");
    }
    const animation = el.animate(keyframes, __spreadProps(__spreadValues({}, options), {
      duration: prefersReducedMotion() ? 0 : options.duration
    }));
    animation.addEventListener("cancel", resolve, { once: true });
    animation.addEventListener("finish", resolve, { once: true });
  });
}
function parseDuration(delay) {
  delay = (delay + "").toLowerCase();
  if (delay.indexOf("ms") > -1) {
    return parseFloat(delay);
  }
  if (delay.indexOf("s") > -1) {
    return parseFloat(delay) * 1e3;
  }
  return parseFloat(delay);
}
function prefersReducedMotion() {
  const query2 = window.matchMedia("(prefers-reduced-motion: reduce)");
  return query2 == null ? void 0 : query2.matches;
}
function stopAnimations(el) {
  return Promise.all(el.getAnimations().map((animation) => {
    return new Promise((resolve) => {
      const handleAnimationEvent = requestAnimationFrame(resolve);
      animation.addEventListener("cancel", () => handleAnimationEvent, { once: true });
      animation.addEventListener("finish", () => handleAnimationEvent, { once: true });
      animation.cancel();
    });
  }));
}

// src/utilities/animation-reqistry.ts
var defaultAnimationRegistry = /* @__PURE__ */ new Map();
var customAnimationRegistry = /* @__PURE__ */ new WeakMap();
function ensureAnimation(animation) {
  return animation != null ? animation : { keyframes: [], options: { duration: 0 } };
}
function setDefaultAnimation(animationName, animation) {
  defaultAnimationRegistry.set(animationName, ensureAnimation(animation));
}
function getAnimation(el, animationName) {
  const customAnimation = customAnimationRegistry.get(el);
  if (customAnimation && customAnimation[animationName]) {
    return customAnimation[animationName];
  }
  const defaultAnimation = defaultAnimationRegistry.get(animationName);
  if (defaultAnimation) {
    return defaultAnimation;
  }
  return {
    keyframes: [],
    options: { duration: 0 }
  };
}

// src/utilities/overlay-component.ts
import { html, css } from "lit";
import { property, query, state } from "lit/decorators.js";
import {
  createPopper
} from "@popperjs/core";

// src/utilities/elements-utils.ts
function getChildren(el) {
  const children = [];
  for (let index = 0; index < el.children.length; index++) {
    const childEl = el.children.item(index);
    if (childEl)
      children.push(childEl);
  }
  return children;
}

// src/utilities/overlay-component.ts
var ComponentsWithOverlayMinWidthOfTarget = ["yotpo-popover"];
var ComponentsWithoutHoverSpecialBehavior = ["yotpo-popover"];
var id = 0;
var OverlayComponent = class extends NoInnerEventsComponent {
  constructor() {
    super(...arguments);
    this.possibleSlots = [];
    this.overlayId = `overlay-${++id}`;
    this.isMinWidthModifierNeeded = false;
    this.isTooltipHover = false;
    this.shown = false;
    this.placement = "top" /* top */;
    this.distance = 0;
    this.flip = true;
    this.open = false;
    this.visibilityEnabled = true;
    this.trigger = "hover" /* hover */ + " " + "focus" /* focus */;
    this.hoist = true;
    this.remainOpenOnTargetClick = false;
    this.boundaryElement = null;
    this.disabled = false;
  }
  connectedCallback() {
    super.connectedCallback();
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleTargetBlur = this.handleTargetBlur.bind(this);
    this.handleTargetClick = this.handleTargetClick.bind(this);
    this.handleTargetFocus = this.handleTargetFocus.bind(this);
    this.handleTargetKeyDown = this.handleTargetKeyDown.bind(this);
    this.handleTargetMouseOver = this.handleTargetMouseOver.bind(this);
    this.handleTargetMouseOut = this.handleTargetMouseOut.bind(this);
    this.updateComplete.then(() => {
      this.addEventListener("blur" /* blur */, this.handleTargetBlur, true);
      this.addEventListener("focus" /* focus */, this.handleTargetFocus, true);
      this.addEventListener("click" /* click */, this.handleTargetClick);
      this.addEventListener("keydown" /* keydown */, this.handleTargetKeyDown);
      this.addEventListener("mouseover" /* mouseover */, this.handleTargetMouseOver);
      this.addEventListener("mouseout" /* mouseout */, this.handleTargetMouseOut);
      document.addEventListener("click" /* click */, this.handleDocumentClick);
      this.target = this.getTarget();
      this.isMinWidthModifierNeeded = ComponentsWithOverlayMinWidthOfTarget.includes(this.tagName.toLowerCase());
      this.syncOptions();
    });
  }
  async firstUpdated() {
    this.handleOpenChange(false);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("blur" /* blur */, this.handleTargetBlur, true);
    this.removeEventListener("focus" /* focus */, this.handleTargetFocus, true);
    this.removeEventListener("click" /* click */, this.handleTargetClick);
    this.removeEventListener("keydown" /* keydown */, this.handleTargetKeyDown);
    this.removeEventListener("mouseover" /* mouseover */, this.handleTargetMouseOver);
    this.removeEventListener("mouseout" /* mouseout */, this.handleTargetMouseOut);
    document.removeEventListener("click" /* click */, this.handleDocumentClick);
    if (this.overlayContainer) {
      this.overlayContainer.destroy();
    }
  }
  async show() {
    if (!this.disabled && this.visibilityEnabled && !this.shown) {
      this.shown = true;
      this.handleShownChange();
      return waitForEvent(this, "yotpo-show" /* show */);
    }
  }
  async hide() {
    if (!this.shown || !this.visibilityEnabled || this.isTooltipTriggeredFromHover) {
      return;
    }
    this.shown = false;
    this.handleShownChange();
    return waitForEvent(this, "yotpo-hide" /* hide */);
  }
  handleTargetBlur() {
    if (this.hasTrigger("focus" /* focus */)) {
      this.hide();
    }
  }
  handleTargetClick() {
    if (this.hasTrigger("click" /* click */)) {
      if (this.shown && !this.remainOpenOnTargetClick) {
        this.hide();
      } else if (!this.shown) {
        this.show();
      }
    }
  }
  handleTargetFocus() {
    if (this.hasTrigger("focus" /* focus */)) {
      this.show();
    }
  }
  handleTargetKeyDown(event) {
    if (this.shown && event.key === "Escape") {
      event.stopPropagation();
      this.hide();
    }
  }
  handleTargetMouseOver() {
    if (this.hasTrigger("hover" /* hover */)) {
      const delay = parseDuration(getComputedStyle(this).getPropertyValue("--show-delay"));
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
      this.hoverTimeout = setTimeout(() => this.show(), delay);
    }
  }
  handleTargetMouseOut() {
    if (this.hasTrigger("hover" /* hover */)) {
      const delay = parseDuration(getComputedStyle(this).getPropertyValue("--hide-delay"));
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
      this.hoverTimeout = setTimeout(() => this.hide(), delay);
    }
  }
  handleDocumentClick(event) {
    if (this.hasTrigger("click" /* click */) && !event.composedPath().includes(this) && this.shown) {
      this.hide();
    }
  }
  getTarget() {
    const target = getChildren(this).find((el) => el.tagName.toLowerCase() !== "style" && !(el.getAttribute("slot") && this.possibleSlots.includes(el.getAttribute("slot"))));
    if (!target) {
      throw new Error("Invalid overlay target: no child element was found.");
    }
    return target;
  }
  handleOpenChange(emitEvent = true) {
    const isShownPresentationChanged = this.shown !== this.open && this.visibilityEnabled;
    this.shown = this.open;
    this.handleShownChange({ emitEvent: emitEvent && isShownPresentationChanged, isShownPresentationChanged });
  }
  async handleShownChange({ emitEvent, isShownPresentationChanged } = { emitEvent: true, isShownPresentationChanged: true }) {
    const shouldShowOverlay = this.shown && this.visibilityEnabled;
    if (shouldShowOverlay) {
      if (isShownPresentationChanged) {
        await stopAnimations(this.overlay);
      }
      if (this.overlayContainer)
        this.overlayContainer.destroy();
      if (!this.target)
        this.target = this.getTarget();
      this.createOverlay();
      this.overlay.hidden = false;
      if (isShownPresentationChanged) {
        const { keyframes, options } = getAnimation(this, "yotpo-overlay.show");
        await animateTo(this.overlay, keyframes, options);
      }
      if (emitEvent) {
        emit(this, "yotpo-show" /* show */);
      }
    } else {
      if (isShownPresentationChanged) {
        await stopAnimations(this.overlay);
        const { keyframes, options } = getAnimation(this, "yotpo-overlay.hide");
        await animateTo(this.overlay, keyframes, options);
      }
      this.overlay.hidden = true;
      if (this.overlayContainer) {
        this.overlayContainer.destroy();
      }
      if (emitEvent) {
        emit(this, "yotpo-hide" /* hide */);
      }
    }
  }
  isShown() {
    return this.shown;
  }
  handlePositioningOptionsChange() {
    this.syncOptions();
  }
  hasTrigger(triggerType) {
    const triggers = this.trigger.split(" ");
    return triggers.includes(triggerType);
  }
  syncOptions() {
    if (!this.overlayContainer)
      return;
    this.overlayContainer.setOptions(this.getPopperOptions());
  }
  updateOverlayContent() {
    if (this.overlayContainer && this.shown && this.visibilityEnabled) {
      this.overlayContainer.update();
    }
  }
  createOverlay() {
    this.overlayContainer = createPopper(this.target, this.positioner, this.getPopperOptions());
  }
  getPopperOptions() {
    const modifiers = [...this.overlayBasicModifiers];
    if (this.isMinWidthModifierNeeded) {
      modifiers.push(__spreadValues({}, this.minWidthModifier));
    }
    const strategy = this.hoist ? "fixed" : "absolute";
    return {
      placement: this.placement,
      strategy,
      modifiers
    };
  }
  get overlayBasicModifiers() {
    const modifiers = [];
    const flipModifier = {
      name: "flip",
      enabled: this.flip,
      options: { rootBoundary: "viewport" }
    };
    const offsetModifier = {
      name: "offset",
      options: {
        offset: [0, this.distance]
      }
    };
    if (this.boundaryElement) {
      flipModifier.options = __spreadProps(__spreadValues({}, flipModifier.options), {
        boundary: this.boundaryElement,
        fallbackPlacements: ["top", "bottom", "left", "right"]
      });
      const preventOverflowModifier = {
        name: "preventOverflow",
        options: {
          boundary: this.boundaryElement
        }
      };
      modifiers.push(preventOverflowModifier);
    }
    modifiers.push(flipModifier, offsetModifier);
    return modifiers;
  }
  get minWidthModifier() {
    const minWidthModifier = {
      name: "minWidth",
      enabled: true,
      fn: ({ state: state2 }) => {
        state2.styles.popper.minWidth = `${state2.rects.reference.width}px`;
      },
      phase: "beforeWrite",
      requires: ["computeStyles"]
    };
    return minWidthModifier;
  }
  get isTooltipTriggeredFromHover() {
    return this.isTooltipHover && !(ComponentsWithoutHoverSpecialBehavior.includes(this.tagName.toLowerCase()) && this.trigger.includes("click" /* click */));
  }
  handlePositionerMouseOver() {
    this.isTooltipHover = true;
  }
  handlePositionerMouseLeave() {
    this.isTooltipHover = false;
  }
  renderOverlay(overlayContent) {
    return html`
			<div class="overlay__content" aria-describedby="${this.overlayId}">
				<slot></slot>
			</div>

			<div class="overlay__positioner">
				<div
					id="${this.overlayId}"
					class="overlay"
					@mouseenter=${this.handlePositionerMouseOver}
					@mouseleave=${this.handlePositionerMouseLeave}
					aria-hidden=${!(this.shown && this.visibilityEnabled)}
				>
					${overlayContent}
				</div>
			</div>
		`;
  }
};
__decorateClass([
  query(".overlay__positioner")
], OverlayComponent.prototype, "positioner", 2);
__decorateClass([
  query(".overlay")
], OverlayComponent.prototype, "overlay", 2);
__decorateClass([
  query('[part="base"]')
], OverlayComponent.prototype, "base", 2);
__decorateClass([
  state()
], OverlayComponent.prototype, "shown", 2);
__decorateClass([
  state()
], OverlayComponent.prototype, "placement", 2);
__decorateClass([
  state()
], OverlayComponent.prototype, "distance", 2);
__decorateClass([
  state()
], OverlayComponent.prototype, "flip", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], OverlayComponent.prototype, "open", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], OverlayComponent.prototype, "visibilityEnabled", 2);
__decorateClass([
  property({ reflect: true })
], OverlayComponent.prototype, "trigger", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], OverlayComponent.prototype, "hoist", 2);
__decorateClass([
  property({ type: Boolean })
], OverlayComponent.prototype, "remainOpenOnTargetClick", 2);
__decorateClass([
  property({ type: HTMLElement })
], OverlayComponent.prototype, "boundaryElement", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], OverlayComponent.prototype, "disabled", 2);
__decorateClass([
  watch("open", { waitUntilFirstUpdate: true })
], OverlayComponent.prototype, "handleOpenChange", 1);
__decorateClass([
  watch("hoist"),
  watch("placement")
], OverlayComponent.prototype, "handlePositioningOptionsChange", 1);
setDefaultAnimation("yotpo-overlay.show", {
  keyframes: [
    { opacity: 0, transform: "scale(0.8)" },
    { opacity: 1, transform: "scale(1)" }
  ],
  options: { duration: 150, easing: "ease" }
});
setDefaultAnimation("yotpo-overlay.hide", {
  keyframes: [
    { opacity: 1, transform: "scale(1)" },
    { opacity: 0, transform: "scale(0.8)" }
  ],
  options: { duration: 150, easing: "ease" }
});
var overlayStyles = css`
	${theme_styles_default}
	${component_styles_default}

	:host {
		--hide-delay: 60ms;
		--show-delay: 150ms;
		display: block;
	}

	.overlay__content {
		display: contents;
	}

	.overlay__positioner {
		position: absolute;
		z-index: var(--yotpo-z-index-tooltip);
	}
`;

export {
  animateTo,
  stopAnimations,
  setDefaultAnimation,
  getAnimation,
  OverlayComponent,
  overlayStyles
};
